import { useMemo } from "react";

import { CommunicationPreferenceFormType, Locales, Nullable } from "@simplyk/common";

import { trpc } from "@/common/helpers/trpc";
import { useCommunicationPreferencesDraft } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/CommunicationPreferences/helpers/communicationPreferencesDraft";
import { useCommunicationPreferencesFlag } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/hooks/useCommunicationPreferencesFlag";

// Only the public page endpoint puts options on the form object, so the preview has to fetch them itself.
export const useCommunicationPreferencesForPreview = (
  formId: string,
  formType: CommunicationPreferenceFormType,
  enabled = true
): {
  isLoaded: boolean;
  communicationPreferences: { contactListId: string; name: string }[];
  questionTitle: Nullable<string>;
} => {
  const { isActive } = useCommunicationPreferencesFlag();
  const { data } = trpc.getFormCommunicationPreferences.useQuery(
    { formId, formType },
    { enabled: isActive && enabled }
  );
  const draft = useCommunicationPreferencesDraft(formId, formType);
  const selectedContactListIds = draft?.selectedContactListIds ?? data?.selectedContactListIds;

  const communicationPreferences = useMemo(() => {
    if (!data || !selectedContactListIds) {
      return [];
    }
    return selectedContactListIds.flatMap((contactListId) => {
      const option = data.options.find((candidate) => candidate.contactListId === contactListId);
      return option ? [{ contactListId, name: option.name }] : [];
    });
  }, [data, selectedContactListIds]);

  return {
    isLoaded: Boolean(data),
    communicationPreferences,
    questionTitle: draft ? draft.questionTitle : (data?.questionTitle ?? null),
  };
};

// The editor previews the main language, the one whose title the communication preferences modal edits.
export const withPreviewQuestionTitle = <Field extends { locale: Locales }>(
  fields: Field[],
  locale: Locales,
  questionTitle: Nullable<string>
): Field[] =>
  fields.map((field) => (field.locale === locale ? { ...field, communicationPreferencesTitle: questionTitle } : field));
